import React, { useCallback, useEffect, useMemo, useState } from 'react';
import i18n from '../../../utilities/i18n';
import { Table, Button, Tooltip } from '@adc/parallax-component-library';
import DynamicTable, {
  CheckboxFilterSection,
  ColumnConfig,
} from '../../table/DynamicTable';
import { AccountWithRoleAssignments, Role, Account } from '../../../models';
import RoleAssignmentsModal from '../../modal/RoleAssignmentsModal';
import { useAppSelector } from '../../../reducers/hooks';

interface RoleAssignmentsTableProps {
  allAccounts: Account[];
  accountsWithRoleAssignments: AccountWithRoleAssignments[];
  roles: Role[];
  isLoading: boolean;
  isError: boolean;
  onSaveRoleChanges: (
    account: AccountWithRoleAssignments,
    addRoleKeys: string[],
    removeRoleKeys: string[],
  ) => Promise<void>;
  isSavingRoleChanges?: boolean;
  headerAction?: React.ReactElement;
}

const getAccountFullName = (account: AccountWithRoleAssignments) => {
  return `${account.firstName} ${account.lastName}`;
};

const getAccountRoleNames = (account: AccountWithRoleAssignments) => {
  return account.roleAssignments
    .map((roleAssignment) => roleAssignment.role.name)
    .join(', ');
};

/**
 * Table for displaying user accounts with role assignments
 * @param {Account[]} allAccounts - All accounts available for selection when adding a new role assignment
 * @param {AccountWithRoleAssignments[]} accountsWithRoleAssignments - Accounts with role assignments
 * @param {Role[]} roles - Available roles, used for filtering and for role assignment
 * @param {boolean} isLoading - Data loading state
 * @param {boolean} isError - Data error state
 * @param {(account: AccountWithRoleAssignments, addRoleKeys: string[], removeRoleKeys: string[]) => Promise<void>} onSaveRoleChanges - Callback when saving role changes
 * @param {boolean} isSavingRoleChanges - Whether a save is in progress
 * @param {React.ReactElement} headerAction - Optional element rendered in the table header
 * @returns {React.JSX.Element}
 */
export default function RoleAssignmentsTable({
  allAccounts = [],
  accountsWithRoleAssignments,
  roles,
  isLoading,
  isError,
  onSaveRoleChanges,
  isSavingRoleChanges = false,
  headerAction,
}: RoleAssignmentsTableProps) {
  const [filteredAccounts, setFilteredAccounts] = useState<
    AccountWithRoleAssignments[]
  >([]);
  const [selectedTableRow, setSelectedTableRow] =
    useState<AccountWithRoleAssignments | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { canInsertAssignments } = useAppSelector(
    (state) => state.capabilities,
  );

  const allAccountsAssigned =
    allAccounts.length > 0 &&
    allAccounts.every((account) =>
      accountsWithRoleAssignments.some((a) => a.key === account.key),
    );

  const initialCheckboxFilters = useMemo(() => {
    const items = roles.map((role) => ({
      id: role.key,
      label: role.name,
      value: false,
    }));

    return [
      {
        id: 'role',
        label: i18n.t('Page_RoleAssignments.roleAssignments'),
        items,
      },
    ];
  }, [roles]);

  const [checkboxFilters, setCheckboxFilters] = useState<
    CheckboxFilterSection[]
  >([]);

  useEffect(() => {
    let filtered = accountsWithRoleAssignments;

    checkboxFilters.forEach((filter) => {
      const activeFilterValues = filter.items
        .filter((item) => item.value)
        .map((item) => item.id);

      if (activeFilterValues.length > 0 && filter.id === 'role') {
        filtered = filtered.filter((account) => {
          const hasNoRoles = account.roleAssignments.length === 0;
          const hasSelectedRoles = account.roleAssignments.some(
            (roleAssignment) =>
              activeFilterValues.includes(roleAssignment.role.key),
          );

          return (
            (activeFilterValues.includes('unassigned') && hasNoRoles) ||
            hasSelectedRoles
          );
        });
      }
    });

    setFilteredAccounts(filtered);
  }, [checkboxFilters, accountsWithRoleAssignments]);

  useEffect(() => {
    setCheckboxFilters(initialCheckboxFilters);
  }, [initialCheckboxFilters]);

  const columnData: ColumnConfig[] = useMemo(
    () =>
      [
        {
          label: i18n.t('Page_RoleAssignments.name'),
          dataKey: 'name',
          getSortValue: (account: AccountWithRoleAssignments) =>
            getAccountFullName(account),
        },
        { label: i18n.t('Account.email'), dataKey: 'email' },
        {
          label: i18n.t('Page_RoleAssignments.roleAssignments'),
          dataKey: 'roles',
          getSortValue: (account: AccountWithRoleAssignments) =>
            getAccountRoleNames(account),
        },
        canInsertAssignments
          ? { label: '', dataKey: 'action', isSortable: false }
          : null,
      ].filter(Boolean) as ColumnConfig[],
    [canInsertAssignments],
  );

  const handleFiltersReset = useCallback(() => {
    setCheckboxFilters((prevFilters) =>
      prevFilters.map((filter) => ({
        ...filter,
        items: filter.items.map((item) => ({ ...item, value: false })),
      })),
    );
  }, []);

  const cellRenderer = (
    dataKey: string,
    account: AccountWithRoleAssignments,
  ) => {
    if (dataKey === 'name') {
      return (
        <Table.TextCell key={dataKey}>
          {getAccountFullName(account)}
        </Table.TextCell>
      );
    } else if (dataKey === 'roles') {
      return (
        <Table.TextCell key={dataKey}>
          {getAccountRoleNames(account)}
        </Table.TextCell>
      );
    } else if (dataKey === 'action') {
      return (
        <Table.TextCell key={dataKey}>
          <Button
            variant="tertiary"
            size="small"
            label={i18n.t('Page_RoleAssignments.manageRoleAssignments')}
            onPress={() => {
              if (isSavingRoleChanges) {
                return;
              }

              setSelectedTableRow(account);
              setIsEditModalOpen(true);
            }}
            singleLine
            disabled={isError || isSavingRoleChanges || roles.length === 0}
            testID={`edit-roles-${account.key}`}
          />
        </Table.TextCell>
      );
    } else {
      return <Table.TextCell key={dataKey}>{account[dataKey]}</Table.TextCell>;
    }
  };

  return (
    <>
      <DynamicTable
        testID="account-assignments-table"
        isLoading={isLoading}
        hasLoadingError={isError}
        description={i18n.t('Page_RoleAssignments.table.description')}
        columnData={columnData}
        cellRenderer={cellRenderer}
        rowData={filteredAccounts}
        enableFiltering={true}
        checkboxFilters={checkboxFilters}
        onFiltersChange={setCheckboxFilters}
        onFiltersReset={handleFiltersReset}
        showCard={false}
        toolbarLeftContent={headerAction}
        toolbarRightContent={
          canInsertAssignments ? (
            <Tooltip
              text={i18n.t('Page_RoleAssignments.allAccountsAssigned')}
              open={allAccountsAssigned ? undefined : false}
            >
              <Button
                variant="primary"
                size="medium"
                label={i18n.t('Page_RoleAssignments.addAccount')}
                onPress={() => {
                  if (isSavingRoleChanges) {
                    return;
                  }

                  setSelectedTableRow(null);
                  setIsEditModalOpen(true);
                }}
                disabled={
                  isError ||
                  isSavingRoleChanges ||
                  roles.length === 0 ||
                  allAccountsAssigned
                }
                testID="add-new-user-button"
              />
            </Tooltip>
          ) : null
        }
      />

      <RoleAssignmentsModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedTableRow(null);
        }}
        allAccounts={allAccounts}
        accountsWithRoleAssignments={accountsWithRoleAssignments}
        selectedAccount={selectedTableRow || undefined}
        availableRoles={roles}
        isSaving={isSavingRoleChanges}
        onSave={(account, addRoleKeys, removeRoleKeys) => {
          if (isSavingRoleChanges) {
            return Promise.resolve();
          }

          return onSaveRoleChanges(account, addRoleKeys, removeRoleKeys);
        }}
      />
    </>
  );
}
